import { parseLocalDate, parseLocalDateTime, toLocalISODate, todayLocalDate } from "@/lib/date-utils";
import { parseMoneyBR, roundMoney } from "@/lib/money";

export type PessoaPerformance = {
  id: string;
  nome: string;
  email?: string;
  recebe_meta?: boolean;
  meta_periodo?: string;
  meta_inicio?: string;
  meta_fim?: string;
  meta_valor?: number;
  meta_bonus?: number;
  recebe_comissao?: boolean;
  percentual_comissao?: number;
  base_comissao?: "unidade" | "producao_pessoal";
};

export type AgendamentoPerformance = {
  id: string;
  paciente_id?: string;
  dentista_id?: string;
  data_agendamento: string;
  status?: string;
  valor?: number;
  procedimento?: string;
};

export type OrdemServicoPerformance = {
  id: string;
  paciente_id?: string;
  dentista_id?: string;
  data_abertura?: string | null;
  data_conclusao?: string | null;
  created_at?: string;
  updated_at?: string;
  status?: string;
  valor_total?: number;
  valor_bruto?: number;
  procedimentos?: Array<{ descricao?: string; valor?: number }>;
};

export type ItemProducao = {
  id: string;
  origem: "agendamento" | "ordem_servico";
  dentista_id?: string;
  paciente_id?: string;
  data: string;
  descricao: string;
  valor: number;
};

export const statusRealizado = (status?: string) =>
  ["realizado", "concluido", "concluído", "finalizado", "atendido", "atendida"].includes(String(status || "").trim().toLocaleLowerCase("pt-BR"));

export const statusOrdemServicoProduzida = (status?: string) =>
  ["concluida", "concluída", "finalizada", "finalizado", "realizada", "realizado", "entregue", "paga", "pago"].includes(
    String(status || "").trim().toLocaleLowerCase("pt-BR")
  );

export const competenciaAtual = () => {
  const inicio = todayLocalDate();
  inicio.setDate(1);
  inicio.setHours(0, 0, 0, 0);
  const fim = new Date(inicio);
  fim.setMonth(fim.getMonth() + 1);
  fim.setMilliseconds(-1);
  return { inicio, fim };
};

export const monthInputValue = (date = todayLocalDate()) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

export const periodoDoMes = (mes?: string | Date) => {
  const base = mes instanceof Date ? mes : mes ? parseLocalDate(`${mes}-01`) : todayLocalDate();
  const inicio = new Date(base.getFullYear(), base.getMonth(), 1);
  inicio.setHours(0, 0, 0, 0);
  const fim = new Date(base.getFullYear(), base.getMonth() + 1, 0);
  fim.setHours(23, 59, 59, 999);
  return { inicio, fim };
};

export const periodoPessoa = (pessoa?: PessoaPerformance | null) => {
  const competencia = competenciaAtual();
  const inicio = pessoa?.meta_inicio ? parseLocalDate(pessoa.meta_inicio) : competencia.inicio;
  const fim = pessoa?.meta_fim ? parseLocalDate(pessoa.meta_fim) : competencia.fim;
  inicio.setHours(0, 0, 0, 0);
  fim.setHours(23, 59, 59, 999);
  return { inicio, fim };
};

export const dateInRange = (value: string, inicio: Date, fim: Date) => {
  const date = parseLocalDateTime(value);
  return date >= inicio && date <= fim;
};

export const agendamentosRealizadosNoPeriodo = (agendamentos: AgendamentoPerformance[], inicio: Date, fim: Date) =>
  agendamentos.filter((item) => statusRealizado(item.status) && dateInRange(item.data_agendamento, inicio, fim));

export const producaoDentista = (agendamentos: AgendamentoPerformance[], dentistaId: string, inicio: Date, fim: Date) =>
  agendamentosRealizadosNoPeriodo(agendamentos, inicio, fim)
    .filter((item) => item.dentista_id === dentistaId)
    .reduce((sum, item) => sum + parseMoneyBR(item.valor), 0);

export const producaoClinica = (agendamentos: AgendamentoPerformance[], inicio: Date, fim: Date) =>
  agendamentosRealizadosNoPeriodo(agendamentos, inicio, fim).reduce((sum, item) => sum + parseMoneyBR(item.valor), 0);

const dataOrdemServico = (ordem: OrdemServicoPerformance) =>
  ordem.data_conclusao || ordem.updated_at || ordem.created_at || ordem.data_abertura || "";

export const ordensServicoProduzidasNoPeriodo = (ordens: OrdemServicoPerformance[], inicio: Date, fim: Date) =>
  ordens.filter((item) => statusOrdemServicoProduzida(item.status) && dateInRange(dataOrdemServico(item), inicio, fim));

const valorOrdemServico = (ordem: OrdemServicoPerformance) => {
  const total = parseMoneyBR(ordem.valor_total || ordem.valor_bruto);
  if (total > 0) return total;
  return roundMoney((ordem.procedimentos || []).reduce((sum, procedimento) => sum + parseMoneyBR(procedimento.valor), 0));
};

const diaDaProducao = (value: string) => value ? toLocalISODate(parseLocalDateTime(value)) : "";

// Some legacy workflows create both an appointment and an OS for the same service.
// Keep the appointment as the source of truth when all identifying data matches.
const representaMesmoServico = (agendamento: ItemProducao, ordem: ItemProducao) =>
  agendamento.dentista_id === ordem.dentista_id
  && !!agendamento.paciente_id
  && agendamento.paciente_id === ordem.paciente_id
  && agendamento.data === ordem.data
  && Math.abs(agendamento.valor - ordem.valor) < 0.005;

export const itensProducaoNoPeriodo = (
  agendamentos: AgendamentoPerformance[],
  ordens: OrdemServicoPerformance[],
  inicio: Date,
  fim: Date
) => {
  const atendimentos: ItemProducao[] = agendamentosRealizadosNoPeriodo(agendamentos, inicio, fim)
    .map((item) => ({
      id: item.id,
      origem: "agendamento" as const,
      dentista_id: item.dentista_id,
      paciente_id: item.paciente_id,
      data: diaDaProducao(item.data_agendamento),
      descricao: item.procedimento || "Atendimento realizado",
      valor: parseMoneyBR(item.valor),
    }))
    .filter((item) => item.valor > 0);

  const ordensConcluidas: ItemProducao[] = ordensServicoProduzidasNoPeriodo(ordens, inicio, fim)
    .map((item) => ({
      id: item.id,
      origem: "ordem_servico" as const,
      dentista_id: item.dentista_id,
      paciente_id: item.paciente_id,
      data: diaDaProducao(dataOrdemServico(item)),
      descricao: item.procedimentos?.map((procedimento) => procedimento.descricao).filter(Boolean).join(", ") || "Ordem de serviço concluída",
      valor: valorOrdemServico(item),
    }))
    .filter((item) => item.valor > 0);

  return [...atendimentos, ...ordensConcluidas.filter((ordem) => !atendimentos.some((agendamento) => representaMesmoServico(agendamento, ordem)))];
};

export const producaoDentistaCompleta = (
  agendamentos: AgendamentoPerformance[],
  ordens: OrdemServicoPerformance[],
  dentistaId: string,
  inicio: Date,
  fim: Date
) => roundMoney(itensProducaoNoPeriodo(agendamentos, ordens, inicio, fim)
  .filter((item) => item.dentista_id === dentistaId)
  .reduce((sum, item) => sum + item.valor, 0));

export const producaoClinicaCompleta = (
  agendamentos: AgendamentoPerformance[],
  ordens: OrdemServicoPerformance[],
  inicio: Date,
  fim: Date
) => roundMoney(itensProducaoNoPeriodo(agendamentos, ordens, inicio, fim).reduce((sum, item) => sum + item.valor, 0));

export const calcularComissao = (producao: number, percentual?: number) =>
  roundMoney(parseMoneyBR(producao) * Number(percentual || 0) / 100);

export const calcularMeta = (pessoa: PessoaPerformance | null | undefined, producao: number) => {
  const valor = Number(pessoa?.meta_valor || 0);
  const bonus = Number(pessoa?.meta_bonus || 0);
  const percentual = valor > 0 ? Math.min(100, (Number(producao || 0) / valor) * 100) : 0;
  const bateu = valor > 0 && Number(producao || 0) >= valor;
  return {
    valor,
    produzido: Number(producao || 0),
    bonusPrevisto: bonus,
    bonusLiberado: bateu ? bonus : 0,
    percentual,
    falta: Math.max(0, valor - Number(producao || 0)),
    bateu,
  };
};

export const periodoToISO = (inicio: Date, fim: Date) => ({
  inicio: toLocalISODate(inicio),
  fim: toLocalISODate(fim),
});
